import {
  EnvironmentProviders,
  inject,
  makeEnvironmentProviders,
  NgZone,
  provideEnvironmentInitializer,
} from '@angular/core';
import { Analytics as FirebaseAnalytics, getAnalytics } from 'firebase/analytics';
import { FirebaseApp, FirebaseOptions, getApp, getApps, initializeApp } from 'firebase/app';
import { Auth as FirebaseAuth, getAuth } from 'firebase/auth';
import { Firestore as FirebaseFirestore, getFirestore } from 'firebase/firestore';
import { Functions as FirebaseFunctions, getFunctions } from 'firebase/functions';
import { FirebasePerformance, getPerformance } from 'firebase/performance';
import { FirebaseStorage, getStorage } from 'firebase/storage';
import { attachAngularZone } from './rxjs';
import { Analytics, App, Auth, Firestore, Functions, Performance, Storage } from './tokens';

export function provideFirebaseApp(factory: () => FirebaseApp): EnvironmentProviders {
  return makeEnvironmentProviders([
    {
      provide: App,
      useFactory: factory,
    },
    // Re-enter Angular zone for Firestore/Auth observables (stuck spinner safety net).
    provideEnvironmentInitializer(() => {
      attachAngularZone(inject(NgZone));
    }),
  ]);
}

export function provideAuth(
  factory: (app: FirebaseApp) => FirebaseAuth = (app) => getAuth(app),
): EnvironmentProviders {
  return makeEnvironmentProviders([
    {
      provide: Auth,
      useFactory: factory,
      deps: [App],
    },
  ]);
}

export function provideFirestore(
  factory: (app: FirebaseApp) => FirebaseFirestore = (app) => getFirestore(app),
): EnvironmentProviders {
  return makeEnvironmentProviders([
    {
      provide: Firestore,
      useFactory: factory,
      deps: [App],
    },
  ]);
}

export function provideFunctions(
  factory: (app: FirebaseApp) => FirebaseFunctions = (app) => getFunctions(app),
): EnvironmentProviders {
  return makeEnvironmentProviders([
    {
      provide: Functions,
      useFactory: factory,
      deps: [App],
    },
  ]);
}

export function provideStorage(
  factory: (app: FirebaseApp) => FirebaseStorage = (app) => getStorage(app),
): EnvironmentProviders {
  return makeEnvironmentProviders([
    {
      provide: Storage,
      useFactory: factory,
      deps: [App],
    },
  ]);
}

export function provideAnalytics(
  factory: (app: FirebaseApp) => FirebaseAnalytics = (app) => getAnalytics(app),
): EnvironmentProviders {
  return makeEnvironmentProviders([
    {
      provide: Analytics,
      useFactory: factory,
      deps: [App],
    },
    provideEnvironmentInitializer(() => {
      inject(Analytics);
    }),
  ]);
}

export function providePerformance(
  factory: (app: FirebaseApp) => FirebasePerformance = (app) => getPerformance(app),
): EnvironmentProviders {
  return makeEnvironmentProviders([
    {
      provide: Performance,
      useFactory: factory,
      deps: [App],
    },
    provideEnvironmentInitializer(() => {
      inject(Performance);
    }),
  ]);
}

export function ensureApp(options?: FirebaseOptions) {
  if (getApps().length) {
    return getApp();
  }
  if (!options) {
    throw new Error('Firebase app is not initialized and no options were provided.');
  }
  return initializeApp(options);
}

export {
  getAnalytics,
  getAuth,
  getFirestore,
  getFunctions,
  getPerformance,
  getStorage,
  initializeApp,
};
